import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Box,
  Stack,
  Avatar,
  Typography,
  IconButton,
  Tooltip,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Fade
} from "@mui/material";
import { Send } from "@mui/icons-material";
import { MentionsInput, Mention } from "react-mentions";
import { motion } from "framer-motion";
import ExpandableText from "./ExpandableText";
import { mentionsStyleComments } from "./mentionsStyles";
import { REACCIONES_MAP } from "./ReaccionesConstants";

const CommentItem = ({
  comm,
  userAvatar,
  currentUserId,
  queryUsers,
  onCommentSubmit,
  onCommentReaction,
  onCommentDelete,
  postOwnerId,
}) => {
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [anchorReactions, setAnchorReactions] = useState(null);
  
  // Timers para controlar el hover exacto
  const openTimerRef = useRef(null);
  const closeTimerRef = useRef(null);

  const canDelete = Number(currentUserId) === Number(comm.user_id) || Number(currentUserId) === Number(postOwnerId);

  // Limpieza de timers al desmontar
  useEffect(() => {
    return () => {
      if (openTimerRef.current) clearTimeout(openTimerRef.current);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  const handleDataFetch = useCallback(
    (query, callback) => {
      if (typeof queryUsers === "function") {
        queryUsers(query, callback);
      } else {
        callback([]);
      }
    },
    [queryUsers]
  );

  const handleSendReply = async () => {
    if (!replyText.trim()) return;
    const success = await onCommentSubmit(replyText, comm.id);
    if (success) {
      setReplyText("");
      setShowReplyInput(false);
    }
  };

  // --- LÓGICA DE HOVER MEJORADA ---
  const handleMouseEnter = (e) => {
    const target = e.currentTarget;
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    
    // Espera 1 segundo antes de abrir
    openTimerRef.current = setTimeout(() => {
      setAnchorReactions(target);
    }, 700); 
  };

  const handleMouseLeave = () => {
    if (openTimerRef.current) clearTimeout(openTimerRef.current);
    
    // Tarda 0.5 segundos en cerrar
    closeTimerRef.current = setTimeout(() => {
      setAnchorReactions(null);
    }, 500);
  };

  const ReactionsPeopleList = (
    <Box sx={{ p: 0.5, maxWidth: 220 }}>
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, px: 1, py: 0.5, display: "block", borderBottom: "1px solid #eee", mb: 0.5, color: "#1c1e21" }}
      >
        Reacciones
      </Typography>
      <List dense sx={{ py: 0 }}>
        {comm.reactions_detail?.map((reac, idx) => (
          <ListItem key={idx} sx={{ px: 1, py: 0.5 }}>
            <ListItemAvatar sx={{ minWidth: 32 }}>
              <Box sx={{ position: "relative" }}>
                <Avatar src={reac.avatar} sx={{ width: 24, height: 24 }} />
                <Box
                  sx={{
                    position: "absolute", right: -4, bottom: -4,
                    bgcolor: "white",
                    borderRadius: "50%", width: 14, height: 14,
                    display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #fff",
                  }}
                >
                  <Box component="img" src={REACCIONES_MAP[reac.reaction_type]?.img} sx={{ width: 10, height: 10 }} />
                </Box>
              </Box>
            </ListItemAvatar>
            <ListItemText
              primary={`${reac.nombre} ${reac.apellido}`}
              primaryTypographyProps={{ fontSize: "0.75rem", fontWeight: 500, color: "#1c1e21" }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  const userReaction = REACCIONES_MAP[comm.user_reaction];

  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction="row" spacing={1}>
        <Avatar src={comm.user_avatar} sx={{ width: 32, height: 32 }} />
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ position: "relative", display: "inline-block", maxWidth: "100%" }}>
            <Box sx={{ bgcolor: "#f0f2f5", p: "8px 12px", borderRadius: "18px" }}>
              <Typography sx={{ fontWeight: 600, fontSize: "0.81rem" }}>{comm.user_full_name}</Typography>
              <ExpandableText text={comm.comment} isComment={true} />
            </Box>

            {comm.total_reactions > 0 && (
              <Tooltip 
                title={ReactionsPeopleList} placement="top" arrow
                componentsProps={{
                  tooltip: { sx: { bgcolor: 'white', color: '#1c1e21', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', border: '1px solid #ddd', p: 0 } },
                  arrow: { sx: { color: 'white', "&::before": { border: "1px solid #ddd", backgroundColor: "white" } } }
                }}
              >
                <Box sx={{
                  position: "absolute", right: -12, bottom: -5, bgcolor: "white", borderRadius: "12px",
                  px: "5px", py: "1px", display: "flex", alignItems: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.2)", border: "1px solid #e0e0e0", cursor: "pointer",
                }}>
                  <Stack direction="row" spacing={-0.4} sx={{ mr: 0.5, alignItems: "center" }}>
                    {comm.reaction_icons?.map((type) => (
                      <Box key={type} component="img" src={REACCIONES_MAP[type]?.img} sx={{ width: 12, height: 12, display: "flex" }} />
                    ))}
                  </Stack>
                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: "0.65rem", color: "#65676b" }}>{comm.total_reactions}</Typography>
                </Box>
              </Tooltip>
            )}
          </Box>

          <Stack direction="row" spacing={2} alignItems="center" sx={{ ml: 1, mt: 0.2 }}>
            <Box 
              onMouseEnter={handleMouseEnter} 
              onMouseLeave={handleMouseLeave} 
              sx={{ position: "relative" }}
            >
              <Typography
                variant="caption"
                sx={{ 
                  fontWeight: 700, cursor: "pointer", fontSize: "0.75rem", 
                  color: userReaction ? userReaction.color : "#65676b", 
                  "&:hover": { textDecoration: "underline" } 
                }}
                onClick={() => onCommentReaction(comm.id, "like")}
              >
                {userReaction ? userReaction.label : "Me gusta"}
              </Typography>

              {/* BARRA DE REACCIONES EMERGENTE */}
              {anchorReactions && (
                <Fade in={Boolean(anchorReactions)}>
                  <Paper 
                    onMouseEnter={() => { if (closeTimerRef.current) clearTimeout(closeTimerRef.current); }}
                    onMouseLeave={handleMouseLeave}
                    sx={{ 
                      position: "absolute", top: -50, left: 0, borderRadius: "25px", 
                      p: "4px 8px", display: "flex", gap: 0.5, 
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)", zIndex: 10,
                      border: '1px solid #eee'
                    }}
                  >
                    {Object.entries(REACCIONES_MAP).map(([key, data]) => (
                      <Tooltip key={key} title={data.label} placement="top" arrow>
                        <motion.div
                          whileHover={{ scale: 1.4, y: -5 }}
                          transition={{ type: "spring", stiffness: 400, damping: 10 }}
                        >
                          <IconButton 
                            size="small" 
                            onClick={() => { onCommentReaction(comm.id, key); setAnchorReactions(null); }} 
                            sx={{ p: 0.5 }}
                          >
                            <Box component="img" src={data.img} sx={{ width: 22, height: 22 }} />
                          </IconButton>
                        </motion.div>
                      </Tooltip>
                    ))}
                  </Paper>
                </Fade>
              )}
            </Box>

            <Typography variant="caption" sx={{ fontWeight: 700, cursor: "pointer", color: "#65676b", fontSize: "0.75rem", "&:hover": { textDecoration: "underline" } }}
              onClick={() => { setShowReplyInput(!showReplyInput); if (!showReplyInput) setReplyText(`@[${comm.user_full_name}](${comm.user_id}) `); }}
            >
              Responder
            </Typography>

            {canDelete && (
              <Typography variant="caption" sx={{ fontWeight: 700, cursor: "pointer", color: "#65676b", fontSize: "0.75rem", "&:hover": { textDecoration: "underline", color: "#f44336" } }}
                onClick={() => onCommentDelete(comm.id)}
              >
                Eliminar
              </Typography>
            )}

            <Typography variant="caption" sx={{ color: "#65676b", fontSize: "0.75rem" }}>
              {new Date(comm.created_at).toLocaleDateString()}
            </Typography>
          </Stack>

          {showReplyInput && (
            <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1 }}>
              <Avatar src={userAvatar} sx={{ width: 24, height: 24 }} />
              <Box sx={{ flexGrow: 1, bgcolor: "#f0f2f5", borderRadius: 3, p: "2px" }}>
                <MentionsInput value={replyText} onChange={(e) => setReplyText(e.target.value)} style={mentionsStyleComments} placeholder="Escribe una respuesta..." allowSuggestionsAboveCursor={true}>
                  <Mention trigger="@" data={handleDataFetch} markup="@[__display__](__id__)" displayTransform={(id, display) => `@${display}`} style={{ backgroundColor: "#D1E7FF" }} />
                </MentionsInput>
              </Box>
              <IconButton size="small" onClick={handleSendReply} sx={{ color: "#1877f2" }}><Send sx={{ fontSize: 18 }} /></IconButton>
            </Stack>
          )}

          {comm.replies?.map((reply) => (
            <Box sx={{ mt: 1, ml: 1, borderLeft: "1px solid #ddd", pl: 1.5 }} key={reply.id}>
              <CommentItem
                comm={reply}
                userAvatar={userAvatar}
                currentUserId={currentUserId}
                queryUsers={queryUsers}
                onCommentSubmit={onCommentSubmit}
                onCommentReaction={onCommentReaction}
                onCommentDelete={onCommentDelete}
              />
            </Box>
          ))}
        </Box>
      </Stack>
    </Box>
  );
};

export default CommentItem;